import { Component, Input, OnDestroy } from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { Subscription } from 'rxjs/Subscription';

import { ChannelInteractionRate } from '../../../../../models/influencer/channel-interaction-rate';
import { UserService } from '../../../../services/user.service';
import { chartConfig } from '../../../../app.constants';

interface ColumnData extends ChannelInteractionRate {
    heightPercentage: string;
    maxWidthPercentage: string;
    tooltip?: string;
    placeholderMode?: boolean;
    color: string;
}

interface DrawData {
    columns: ColumnData[];
    yAxisValues: {
        value: number,
        bottomPercentage: string;
    }[];
}

@Component({
    selector: 'interaction-rate-by-channel',
    templateUrl: './interaction-rate-by-channel.component.html',
    styleUrls: ['./interaction-rate-by-channel.component.scss']
})
export class InteractionRateByChannelComponent implements OnDestroy {
    private _changeSubscription: Subscription;

    public hasNoData = false;
    public drawData: DrawData;
    private readonly _placeholderData: DrawData;

    private readonly _selectedColor: string;

    private _channelInteractionRatesSubject: Subject<ChannelInteractionRate[]>;
    @Input() set channelInteractionRates(value: ChannelInteractionRate[]) {
        this._channelInteractionRatesSubject.next(value);
    }

    private _selectedChannelIdSubject: Subject<number>;
    @Input() set selectedChannelId(value: number) {
        this._selectedChannelIdSubject.next(value);
    }

    @Input() isFetchingData: boolean;

    constructor(userService: UserService) {
        this._selectedColor = userService.chartColors[0];
        this._placeholderData = {
            columns: [
                {
                    channelName: 'Instagram',
                    interactionRate: 2.3,
                    heightPercentage: '23%',
                    maxWidthPercentage: '25%',
                    placeholderMode: true,
                    color: 'rgba(149,160,177, 0.1)'
                },
                {
                    channelName: 'Youtube',
                    interactionRate: 8.6,
                    heightPercentage: '86%',
                    maxWidthPercentage: '25%',
                    placeholderMode: true,
                    color: 'rgba(149,160,177, 0.1)'
                },
                {
                    channelName: 'Blogs',
                    interactionRate: 4.4,
                    heightPercentage: '44%',
                    maxWidthPercentage: '25%',
                    placeholderMode: true,
                    color: 'rgba(149,160,177, 0.1)'
                },
                {
                    channelName: 'Tiktok',
                    interactionRate: 5.1,
                    heightPercentage: '51%',
                    maxWidthPercentage: '25%',
                    placeholderMode: true,
                    color: 'rgba(149,160,177, 0.1)'
                }
            ],
            yAxisValues: [
                {
                    value: 0,
                    bottomPercentage: '0%'
                },
                {
                    value: 2,
                    bottomPercentage: '20%'
                },
                {
                    value: 4,
                    bottomPercentage: '40%'
                },
                {
                    value: 6,
                    bottomPercentage: '60%'
                },
                {
                    value: 8,
                    bottomPercentage: '80%'
                },
                {
                    value: 10,
                    bottomPercentage: '100%'
                }
            ]
        };

        this._channelInteractionRatesSubject = new Subject<ChannelInteractionRate[]>();
        this._selectedChannelIdSubject = new Subject<number>();

        this._changeSubscription = combineLatest([
            this._channelInteractionRatesSubject,
            this._selectedChannelIdSubject
        ]).subscribe({
            next: ([interactionRates, selectedChannelId]) => {
                this.drawData = this.calculateDrawData(interactionRates, selectedChannelId);
            }
        });
    }

    ngOnDestroy(): void {
        this._changeSubscription.unsubscribe();
    }

    private calculateDrawData(interactionRates: ChannelInteractionRate[], selectedChannelId: number): DrawData {
        if (!Array.isArray(interactionRates)
            || interactionRates.length === 0
            || !interactionRates.find(x => x.channelId === selectedChannelId)) {
            this.hasNoData = true;
            return this._placeholderData;
        }

        // show maximum 4 items including selected channel
        const drawnInteractionRates = this.getDrawnChannelInteraction(interactionRates, selectedChannelId, 4);

        // calculate y axis values
        const rates = drawnInteractionRates.map(x => x.interactionRate);
        const maxRate = Math.max(...rates);
        const step = this.getStepByMaxRate(maxRate);
        const yAxisCount = Math.ceil(maxRate / step);
        const maxY = yAxisCount * step;
        const isZeroData = maxY === 0;
        const yAxisValues = [];

        if (isZeroData) {
            yAxisValues.push({
                value: 0,
                bottomPercentage: `0%`,
            });
        } else {
            for (let i = 0; i <= maxY; i += step) {
                yAxisValues.push({
                    value: i,
                    bottomPercentage: `${i / maxY * 100}%`,
                });
            }
        }

        const unselectedColorDelta = drawnInteractionRates[0].channelId === selectedChannelId ? 1 : 0;
        const unSelectedColors = chartConfig.unselectedColors;
        const maxWidthPercentage = `${100 / drawnInteractionRates.length}%`;
        const columns = drawnInteractionRates.map((interactionRate, index) => {
            return {
                ...interactionRate,
                heightPercentage: `${isZeroData ? 0 : (interactionRate.interactionRate / maxY * 100)}%`,
                maxWidthPercentage,
                tooltip: `${interactionRate.platformName}: ${interactionRate.channelName}`,
                color: interactionRate.channelId === selectedChannelId
                    ? this._selectedColor
                    : unSelectedColors[(index - unselectedColorDelta) % unSelectedColors.length]
            };
        });

        this.hasNoData = false;
        return { columns, yAxisValues };
    }

    private getDrawnChannelInteraction(interactionRates: ChannelInteractionRate[], selectedChannelId: number, maxItemsCount: number): ChannelInteractionRate[] {
        const selectedChannelInteractionRate = interactionRates.find(x => x.channelId === selectedChannelId);
        const hasSelectedChannel = !!selectedChannelInteractionRate;
        const others = interactionRates.filter(x => x !== selectedChannelInteractionRate);
        const sorted = others.sort((a, b) => b.interactionRate - a.interactionRate);

        if (hasSelectedChannel) {
            const top3Sorted = sorted.slice(0, maxItemsCount - 1);
            const top3 = interactionRates.filter(x => top3Sorted.indexOf(x) >= 0);
            return [selectedChannelInteractionRate, ...top3];
        }

        const top4Sorted = sorted.slice(0, maxItemsCount);
        return interactionRates.filter(x => top4Sorted.indexOf(x) >= 0);
    }

    private getStepByMaxRate(maxRate: number): number {
        const availableSteps = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20];
        const step = availableSteps.find(s => maxRate <= 5 * s);

        if (step) {
            return step;
        }

        return 50;
    }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/influencer-insight/interaction-rate-by-channel/interaction-rate-by-channel.component.ts